import {SelectionsOverview} from 'components/customer/JobDashboard/columns/SelectionsOverview';
import {useEditProduct} from 'components/customer/RoomPlanner/components/FloatingMenu/Actions/useEditProduct';
import {Specs} from 'components/customer/RoomPlanner/components/FloatingMenu/Specs';
import {ButtonContainer} from 'components/customer/RoomPlanner/components/Menu/Header';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {useJobContext} from 'contexts/JobContext';
import React, {useCallback} from 'react';
import {useSearchParams} from 'react-router-dom';
import {
    touchDevices,
    mobileLandscapeAndPortraitDevices,
    mobilePortrait,
} from 'shared/Breakpoints';
import {CBCButton, Icon, formatPrice} from 'shared/helpers';
import {PartialJob} from 'shared/types/PartialJob';
import {useAppSelector} from 'store/customer';
import {
    selectPageRestricted,
    selectRestrictionMessageDismissed,
} from 'store/customer/appSlice';
import styled from 'styled-components';
import {useRoomPreview} from 'components/customer/RoomPlanner/components/helpers/RoomPreview';
import {getRoomPlannerRoomIndex} from 'components/customer/RoomPlanner/store/roomPlannerSlice';

interface ProductDropdownProps {
    infoDisplayOverride?: boolean;
}

export const ProductDropdown = ({
    infoDisplayOverride,
}: ProductDropdownProps) => {
    const {roomIndex: contextRoomIndex} = useJobContext() as PartialJob;
    const reduxRoomIndex = useAppSelector(getRoomPlannerRoomIndex);
    const {selectedProduct, selectedCabinetGroup, setSelectedCabinetGroup} =
        useRoomPlannerContext();
    const isRoomPreview = useRoomPreview();
    const {handleEditClick} = useEditProduct();

    const isRestricted = useAppSelector(selectPageRestricted);
    const messageDismissed = useAppSelector(selectRestrictionMessageDismissed);

    const [, setSearchParams] = useSearchParams();

    const handleCancel = useCallback(() => {
        setSearchParams({});
        setSelectedCabinetGroup?.(null);
    }, []);

    if (!selectedProduct || selectedCabinetGroup?.userData?.isPlaceholder) {
        return null;
    }

    const productName = !!selectedProduct?.cabinet_note
        ? selectedProduct?.cabinet_note
        : selectedProduct?.type_name;

    // In preview mode, use Redux roomIndex (set from preview data)
    // In normal mode, use JobContext roomIndex
    const roomIndex = isRoomPreview ? reduxRoomIndex : contextRoomIndex;

    const roomPrefix =
        roomIndex != null && !Number.isNaN(Number(roomIndex))
            ? `${Number(roomIndex) + 1}-`
            : '';

    return (
        <DropdownContainer
            $hasTopOffset={(isRestricted && !messageDismissed) || false}>
            <RowOne>
                <ProductNumberAndName>
                    {`[${roomPrefix}${selectedProduct.room_cab_number}] ${productName}`}
                </ProductNumberAndName>
                {isRoomPreview ? null : (
                    <ProductCost>
                        {formatPrice(
                            selectedProduct.cost / selectedProduct.quantity,
                            {}
                        )}
                    </ProductCost>
                )}
                <CloseControl>
                    <ButtonContainer onClick={handleCancel}>
                        <Icon iconName="Button-Cancel.svg" />
                    </ButtonContainer>
                </CloseControl>
            </RowOne>

            <RowThree>
                <SelectionsOverview
                    product={selectedProduct}
                    infoDisplayOverride={infoDisplayOverride}
                />
                <ProductDimensions>
                    <Specs product={selectedProduct} />
                </ProductDimensions>
            </RowThree>

            <ButtonRow>
                <Button
                    onClick={handleCancel}
                    iconName="Button-Cancel.svg"
                    className="job-button button-light"
                    style={{color: '#8A8080', flex: 1}}>
                    Cancel
                </Button>
                <CBCButton
                    id="product-submit-button"
                    type="button"
                    onClick={handleEditClick}
                    iconName="Button-Tick.svg"
                    className="job-button button-blue"
                    style={{flex: 1}}>
                    Edit
                </CBCButton>
            </ButtonRow>
        </DropdownContainer>
    );
};

const DropdownContainer = styled.div<{$hasTopOffset: boolean}>`
    background: #ffffff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    color: #000000;
    pointer-events: auto;
    margin-top: 0;
    position: absolute;
    top: 48px;
    left: 5px;
    padding: 10px;
    box-shadow: 2px 2px 10px -1px #bdbdbd;
    width: 400px;
    max-height: calc(100% - 56px);
    overflow: auto;

    ${mobileLandscapeAndPortraitDevices`
        position: initial;
        width: initial;
        max-height: none;
        overflow: visible;
        box-shadow: none;
        padding: 0 10px;
        margin-top: 0;
        background: transparent;
    `}

    ${({$hasTopOffset}) => {
        if ($hasTopOffset) {
            return `
                top: 103px;
            `;
        }
    }};
`;

const RowOne = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    color: ${({theme}) => theme.colors.secondary.main};
    font-size: 16px;
    font-weight: 600;
    min-width: 0;
    border-bottom: 1px solid ${({theme}) => theme.colors.secondary.main};
    padding-bottom: 8px;
`;

const CloseControl = styled.div`
    flex-shrink: 0;

    ${mobileLandscapeAndPortraitDevices`
        display: none;
    `}
`;

const ProductNumberAndName = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
`;

const ProductDimensions = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    align-items: center;
    p {
        margin: 0;
        font-size: 14px;
        font-weight: 400;
        color: black;
    }
`;

const ProductCost = styled.div`
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
`;

const RowThree = styled.div`
    padding: 12px 0;
    display: flex;
    flex-direction: column;

    ${touchDevices`
        flex-direction: row;
        gap: 10px;
        align-items: flex-start;

        > div:first-child {
            width: initial;
            max-width: initial;

            > div {
                font-size: 0.65em;
                font-weight: bold;
                max-width: 200px; 
            }
            
            > section {
                width: initial;
                
                > div:first-child {
                    width: initial;
                    flex-shrink: 1;

                    > div {
                        gap: 7px; 
                    }
                    
                    > div > div > div > div {
                        max-width: initial;
                    }
                }
            }
        }
    `}

    ${mobilePortrait`
        /* Swatches + specs side by side are wider than a portrait phone
           and get clipped by the planner container; let specs wrap below. */
        flex-wrap: wrap;
        row-gap: 12px;

        > div {
            min-width: 0;
        }
    `}

    .selection-container {
        max-width: 100%;
        & > section {
            width: 100%;
            .selection-card {
                flex-grow: 1;
                & > div {
                    gap: 20px;
                }
            }
        }
    }
`;

const ButtonRow = styled.div`
    display: flex;
    gap: 8px;
    width: 100%;

    ${mobileLandscapeAndPortraitDevices`
        display: none;    
    `}
`;
const Button = styled((props: React.ComponentProps<typeof CBCButton>) => (
    <CBCButton {...props} />
))`
    border-radius: 40px !important;
    padding: 5px 10px 5px 5px !important;
    width: fit-content !important;
    font-size: 0.95em !important;
    &:hover {
        border: 1px solid white;
    }
    @media (max-width: 695px) {
        font-size: 0.8em !important;
        width: 80% !important;
    }
`;
